import {
    type RoutePointExtendedType,
    RoutePointsApi,
    type RoutePointSimpleType,
} from '../../../../utilities/RoutePointsApi';
import { z } from 'zod';
import {
    AssignmentJobApi,
    AssignmentJobResponseSchema,
    type AssignmentJobResponseType,
} from '../../AssignmentJobApi';
import { ApiFilterBuilder } from '@automapa/svelte-automapa-template';
import { getFilterAssignmentId } from '../../getFilterAssignmentId';
import { DeliveriesBulkActions } from '../DeliveriesBulkActions';
import type { QueriesType } from '../../../telematics/types';
import { AssignmentJob } from '../../AssignmentJob';
import { AssignmentApi, type AssignmentJobServiceAttributesSimpleType } from '../../AssignmentApi';
import { Api } from '@automapa/svelte-automapa-template';
import { Assignment } from '../../Assignment';
import { JobApi } from '../../../Jobs/JobApi';
import { getRoutePointIfExists } from '../getRoutePointIfExists';
import { DeliveriesMenuActions } from '../DeliveriesMenuActions';
import { sortJobAssignments } from '../sortJobAssignments';
import { PlanningOnMap } from '../../PlanningOnMap';
import { captureMessage } from '../../../../utilities/Sentry';
import { get } from 'svelte/store';
import { features } from '../../../../shared/Features/features';
import { AssignmentJobRejection } from '../../AssignmentJobRejection';
import { AssignmentJobRejectionApi, type AssignmentJobRejectionResponseType } from '../../AssignmentJobRejectionApi';
import { deliveryList } from '../../../../../stores/store';

export class DeliveryListApp {
    api: Api;
    day: string | null = null;
    filteredJobIds: number[] = []; // $filteredItems but only with job ids
    loadedFilteredJobIds: boolean = false;
    items: AssignmentJob[] = [];
    loadedItems: boolean = false;
    private loadingItems: boolean = false;
    assignment: Assignment | null = null;
    assignmentId: number | null = null;
    query: QueriesType = {};
    lastQuery: QueriesType | null = null;
    lastSortBy: any = {};
    routePoints: RoutePointExtendedType[] = [];
    loadedExtendedRoutePoints: boolean = false;
    mapModule: PlanningOnMap | null = null;
    public loading: boolean = false;
    public externalItems: boolean = false;
    projectionFieldsItems: string[] = [
        'job_id,assignment_id,tactical_plan_rule_id,id,suggested_assignment_id,details',
        // 'job',
        'job.id',
        'job.address_attributes',
        // 'job.client_attributes,job.special_attributes',
        'job.special_attributes.delivery_or_collection',
        'job.special_attributes.start_of_time_window',
        'job.special_attributes.end_of_time_window',
        'job.special_attributes.label',
        'job.computed_address',
        'job.rejected,job.target_date,job.process_stage,job.end_date,job.start_date,job.newly_added,job.ad_hoc',
        'job.vehicle_id',
        'job.geom',
    ];
    projectionFieldsFilteredItems: string[] = ['job_id'];

    assignmentRejectedJobs: {[key: number]: AssignmentJobRejection} = {};
    unhandledRejectedJobs: {[key: number]: AssignmentJobRejection} = {};

    private _showSuggestedDeliveries: boolean = true;
    private _showUnassignedDeliveries: boolean = true;
    private _showOnlyInRealisation: boolean = false;

    set(api: Api, scenarioId: number, assignmentId: number): this {
        this.setScenarioId(scenarioId).setAssignmentId(assignmentId).setApi(api);
        return this;
    }

    setShowSuggestedDeliveries(show: boolean): this {
        if (this._showSuggestedDeliveries !== show) {
            this._showSuggestedDeliveries = show;
            this.resetStatus(false);
        }
        return this;
    }

    setShowUnassignedDeliveries(show: boolean): this {
        if (this._showUnassignedDeliveries !== show) {
            this._showUnassignedDeliveries = show;
            this.resetStatus(false);
        }
        return this;
    }

    setShowOnlyInRealisation(show: boolean): this {
        if (this._showOnlyInRealisation !== show) {
            this._showOnlyInRealisation = show;
            this.resetStatus(false);
        }
        return this;
    }

    getShowSuggestedDeliveries(): boolean {
        return this._showSuggestedDeliveries;
    }

    getShowUnassignedDeliveries(): boolean {
        return this._showUnassignedDeliveries;
    }

    getShowOnlyInRealisation(): boolean {
        return this._showOnlyInRealisation;
    }

    getAssignment(): Assignment {
        if (this.assignment === null) {
            throw new Error('Assignment is not set');
        }
        return this.assignment;
    }

    getAssignmentJobByJobId(jobId: number): AssignmentJob | null {
        return this.items.find(j => j.getJobId() === jobId) ?? null;
    }

    setApi(api: Api): this {
        this.api = api;
        return this;
    }

    setScenarioId(scenarioId: number | null): this {
        if (scenarioId !== this.assignment?.getScenarioId()) {
            // this.loadingItems = false;
            if (this.externalItems) {
                this.items = [];
                this.filteredJobIds = [];
                this.externalItems = false;
            }
            this.loadedItems = false;
            this.loadedFilteredJobIds = false;
        }
        this.assignment?.setScenarioId(scenarioId);
        return this;
    }

    async load() {
        if (this.loadingItems) {
            console.warn('DeliveryListApp, load, already loading');
            return;
        }
        if (this.loadedItems && this.loadedFilteredJobIds) {
            return;
        }
        if (this.day === null) {
            console.warn('DeliveryListApp, load, day is not set');
            return;
        }
        this.loadingItems = true;
        if (!this.loadedFilteredJobIds) {
            if (this.assignment === null) {
                await this.populateAssignment();
            }
            await this.populateFilteredIds(this.query);
            this.loadedFilteredJobIds = true;
        }
        await this.populateMissingItems();
        this.removeDuplicatedJobId();
        await this.loadUnhandledRejectedJobs();
        this.loadingItems = false;
    }

    removeDuplicatedJobId() {
        let processedJobIds: number[] = [];

        let uniqueJobIds = this.items.map((a: AssignmentJob) => a.getJobId());
        if (this.items.length !== uniqueJobIds.length) {
            console.warn(
                'DeliveryListApp, removeDuplicatedJobId, duplicated job ids found -- will be hidden'
            );
            captureMessage(
                'DeliveryListApp, removeDuplicatedJobId, duplicated job ids found -- will be hidden',
                {
                    extra: {
                        assignmentId: this.assignmentId,
                        duplicated: this.items.length - uniqueJobIds.length,
                        jobIds: this.items.map((a: AssignmentJob) => a.getJobId()),
                    },
                }
            );
        }

        this.items = this.items.filter((a: AssignmentJob) => {
            let jobId = a.getJobId();
            if (!processedJobIds.includes(jobId)) {
                processedJobIds.push(jobId);
                return true;
            }
            return false;
        });
    }

    async loadExtendedRoutePoints() {
        if (this.assignmentId === null) {
            console.warn('DeliveryListApp, loadExtendedRoutePoints, assignmentId is not set');
            return;
        }
        this.routePoints = [];
        let r = [];
        if (this.assignment?.isRouteCalculated()) {
            r = await RoutePointsApi.getPoints(this.api, this.assignmentId);
            if (r) {
                this.routePoints = r;
                this.loadedExtendedRoutePoints = true;
                this.setRoutePoints(this.routePoints);
            } else {
                console.error('loadRoutePoints, Failed to load route points');
            }
        }
    }

    private updateRoutePointsInAssignmentJobs(routePoints: RoutePointSimpleType[]) {
        routePoints = JSON.parse(JSON.stringify(routePoints));
        if (!this.loadedExtendedRoutePoints) {
            console.warn('Route points are not loaded - call loadExtendedRoutePoints first');
            return;
        }
        this.items = this.items.map(aj => {
            const routePoint = getRoutePointIfExists(routePoints, aj.getJobId());
            if (routePoint) {
                aj.updateRoutePointBySimpleData(routePoint);
            } else {
                // console.warn(
                //     'PageDeliveryList, getRealisationStatus, deliveryListManager, routePoint not found 2 ',
                //     aj.getJobId()
                // );
            }
            return aj;
        });
    }

    getRoutePoints() {
        return JSON.parse(JSON.stringify(this.routePoints));
    }

    setRoutePoints(
        routePoints: RoutePointExtendedType[],
        sortItemsByRoutePoints: boolean = true
    ): this {
        this.routePoints = JSON.parse(JSON.stringify(routePoints));
        this.updateRoutePointsInAssignmentJobs(this.routePoints);
        if (sortItemsByRoutePoints) this.items = sortJobAssignments(this.items, routePoints);
        return this;
    }

    setRoutePointsBySimpleData(simpleRoutePoints: RoutePointSimpleType[]): this {
        if (this.routePoints.length === 0) {
            this.routePoints = JSON.parse(JSON.stringify(simpleRoutePoints));
        } else {
            this.routePoints = this.routePoints.map(rp => {
                let simple = simpleRoutePoints.find(srp => srp.id === rp.id);
                if (simple) {
                    rp.id = simple.id;
                    rp.details = simple.details;
                }
                return rp;
            });
        }
        this.updateRoutePointsInAssignmentJobs(this.routePoints);
        return this;
    }

    setJobServiceAttributesBySimpleData(
        simpleJobServiceAttributes: AssignmentJobServiceAttributesSimpleType[]
    ): this {
        let newRejections = false;
        let item;
        for (let i = 0, ii = this.items.length; i < ii; i++) {
            if (
                (item = simpleJobServiceAttributes.find(
                    sa => sa.job_id === this.items[i].getJobId()
                )) !== undefined
            ) {
                this.items[i].setServiceAttributes(item.attributes || undefined);
                if (item.attributes?.rejection && this.items[i].getJob()) {
                    this.items[i].getJob().rejected = true;
                    newRejections = true;
                }
            }
        }
        if (this.assignmentRejectedJobs) {
            for (let j in this.assignmentRejectedJobs) {
                if (
                    (item = simpleJobServiceAttributes.find(
                    sa => sa.job_id === parseInt(j)
                    )) !== undefined
                ) {
                    this.assignmentRejectedJobs[j].setServiceAttributes(item.attributes || undefined);
                }
            }
        }
        if (newRejections) {
            this.items = [];
            get(deliveryList).triggerReload = true;
        }
        return this;
    }

    // sortJobAssignments(filteredJobs: AssignmentJob[]) {
    //     $filteredJobs = sortJobAssignments($filteredJobs, routePoints);
    // }

    getNextRoutePointJobId(): number[] {
        let routePoint = this.routePoints.find(
            rp =>
                //(typeof rp.details?.approached !== 'number' || rp.details === null) &&
                (rp.details?.status || '') === 'current' &&
                rp.jobs &&
                rp.jobs.length > 0
        );
        if (!routePoint) {
            routePoint = this.routePoints.find(
                rp =>
                    rp.details?.completed === null &&
                    rp.jobs &&
                    rp.jobs.length > 0
            );
        }
        let nextPoints: number[] = [];
        if (routePoint && routePoint.jobs && routePoint.jobs.length > 0) {
            nextPoints = routePoint.jobs;
        }
        return nextPoints;
    }

    async populateAssignment() {
        if (this.assignmentId === null) {
            throw new Error('Assignment ID is not set');
        }
        if (this.day === null) {
            throw new Error('Day is not set, maybe prepareData is missing');
        }
        let r = await AssignmentApi.getOneWithCounters(
            this.api,
            this.assignmentId > 0 ? this.assignmentId : this.day
        );
        // let r = await AssignmentApi.getOne(this.api, this.assignmentId);
        if (r) {
            if (this.assignment) {
                this.assignment.set(r);
            } else {
                this.assignment = new Assignment(r);
            }
        } else {
            throw new Error('Failed to load assignment - see: populateAssignment');
        }
    }

    async populateMissingItems() {
        if (this.loadingItems || !this.externalItems) {
            let missingJobIds = this.filteredJobIds.filter(
                j => !this.items.map(aj => aj.getJobId()).includes(j)
            );
            if (missingJobIds.length === 0) {
                // console.log('DeliveryListApp, populateMissingItems, no missing items');
                return;
            }
            if (this.day === null) {
                throw new Error('Day is not set, maybe prepareData is missing');
            }
            let f = new ApiFilterBuilder(null);
            f.setRelations(['job*']);
            f.setProjectionFields(this.projectionFieldsItems);
            let missingItems =
                this.assignment?.getId()
                    ? await AssignmentJobApi.getForAssignmentWithQueries<AssignmentJobResponseType[]>(
                        this.api,
                        {
                            assignment_id: this.assignment?.getId(),
                            unassigned: this._showUnassignedDeliveries,
                            suggestions: this._showSuggestedDeliveries,
                            query: { job_id: { $in: missingJobIds } },
                            day: this.day,
                        },
                        f,
                        z.array(AssignmentJobResponseSchema)
                    )
                    : await AssignmentJobApi.getWithQueries<AssignmentJobResponseType[]>(
                        this.api,
                        { job_id: { $in: missingJobIds }, x_target_date: this.day },
                        f,
                        z.array(AssignmentJobResponseSchema)
                    );
            // console.log('DisplayAddress, dla', missingItems);
            if (this.loadingItems || !this.externalItems) {
                this.items = this.items.concat(
                    missingItems
                        .map((a: AssignmentJobResponseType) => new AssignmentJob(a))
                        .filter(j => {
                            // return this.filteredJobIds.includes(j.getJobId());
                            // make sure jobId not exists in this.items
                            return !this.items.map(aj => aj.getJobId()).includes(j.getJobId());
                        })
                );
                if (this.loadedExtendedRoutePoints) {
                    this.updateRoutePointsInAssignmentJobs(this.routePoints);
                }
            }
        }
    }

    async populateFilteredIds(query: QueriesType = {}, sortBy: any = {}) {
        if (this.loadingItems || !this.externalItems) {
            if (this.assignment === null) {
                console.warn('Assignment is not set in populateFilteredIds');
                this.filteredJobIds = [];
                return;
            }
            /*if (Object.keys(query).length === 0) {
                // if query is empty, all jobs are shown
                this.filteredJobIds = this.items.map((a: AssignmentJob) => a.getJobId());
                this.lastQuery = JSON.parse(JSON.stringify(query));
                return;
            }*/
            if (this.assignment.isDefault()) {
                query = {
                    assignment_id: this.assignment.getId(),
                    unassigned: !this._showOnlyInRealisation && this._showUnassignedDeliveries,
                    suggestions: !this._showOnlyInRealisation && this._showSuggestedDeliveries,
                    only_in_realisation: this._showOnlyInRealisation,
                    query: query,
                    day: this.day,
                };
            } else {
                query = {
                    ...query,
                    ...getFilterAssignmentId(
                        this._showSuggestedDeliveries,
                        this._showUnassignedDeliveries,
                        this.assignment.isDefault(),
                        this.assignment.getId(),
                        this.day
                    ),
                };
            }
            if (this.lastQuery !== null && JSON.stringify(this.lastQuery) === JSON.stringify(query)) {
                if (
                    !Object.keys(sortBy).length ||
                    JSON.stringify(this.lastSortBy) === JSON.stringify(sortBy)
                ) {
                    // if query is the same as last query, do not load again
                    return;
                }
            }
            if (this.day === null) {
                throw new Error('Day is not set, maybe prepareData is missing');
            }
            let full = this.items.length == 0;
            let f = new ApiFilterBuilder(null);
            f.setRelations(['job*']);
            f.setProjectionFields(full ? this.projectionFieldsItems : ['job_id']);
            if (!sortBy.field || !sortBy.direction) {
                if (this.lastSortBy && Object.keys(this.lastSortBy).length) {
                    sortBy = JSON.parse(JSON.stringify(this.lastSortBy));
                } else {
                    sortBy = {
                        field: 'job.computed_address.city,job.computed_address.street_for_sorting,job.computed_address.house_as_number::int,job.computed_address.house',
                        direction: 'asc,asc,asc,asc',
                    };
                }
            }
            if (sortBy.field && sortBy.direction) {
                f.sortBy(sortBy.field, sortBy.direction);
            }
            // console.log(
            //     '??? DeliveryListApp, populateFilteredIds',
            //     query,
            //     this.lastQuery,
            //     sortBy,
            //     this.lastSortBy,
            //     this.assignment.isDefault()
            // );
            this.lastQuery = JSON.parse(JSON.stringify(query));
            this.lastSortBy = JSON.parse(JSON.stringify(sortBy));
            let onlyWithJobIds;
            if (this.assignment.isDefault()) {
                onlyWithJobIds = await AssignmentJobApi.getForAssignmentWithQueries<
                    { job_id: number }[]
                >(this.api, query, f, z.array(full ? AssignmentJobResponseSchema : AssignmentJobResponseSchema.pick({ job_id: true })));
            } else {
                onlyWithJobIds = await AssignmentJobApi.getWithQueries<{ job_id: number }[]>(
                    this.api,
                    query,
                    f,
                    z.array(full ? AssignmentJobResponseSchema : AssignmentJobResponseSchema.pick({ job_id: true }))
                );
            }
            if (this.loadingItems || !this.externalItems) {
                this.filteredJobIds = onlyWithJobIds.map((a: { job_id: number }) => a.job_id);
                if (full) {
                    this.items = this.items.concat(
                        onlyWithJobIds
                            .map((a: AssignmentJobResponseType) => new AssignmentJob(a))
                            .filter(j => {
                                // return this.filteredJobIds.includes(j.getJobId());
                                // make sure jobId not exists in this.items
                                return !this.items.map(aj => aj.getJobId()).includes(j.getJobId());
                            })
                    );
                }
            }
        }
    }

    async triggerReload(): Promise<this> {
        this.resetStatus(true);
        let currentQuery = JSON.stringify(this.query);
        this.query = {};
        await this.load();
        this.query = JSON.parse(currentQuery);
        if (Object.keys(this.query).length) {
            this.resetStatus(false);
            await this.load();
        }
        return this;
    }

    resetStatus(resetItems: boolean = false): this {
        // this.loadingItems = false;
        if (!this.externalItems) {
            this.externalItems = false;
            this.loadedItems = false;
            this.loadedFilteredJobIds = false;
            if (resetItems) {
                this.routePoints = [];
                this.loadedExtendedRoutePoints = false;
                this.lastQuery = null;
                this.items = [];
                this.filteredJobIds = [];
            }
            this.loadingItems = false;
        }
        return this;
    }

    async asyncGetItems() {
        if (!this.loadedItems) {
            await this.load();
        }
        return this.items;
    }

    setItems(items: AssignmentJob[]) {
        this.externalItems = true;
        this.loadingItems = false;
        this.loadedItems = true;
        this.loadedFilteredJobIds = true;
        this.items = items;
        this.filteredJobIds = items.map((a: AssignmentJob) => a.getJobId());
    }

    async asyncGetFilteredItems() {
        if (!this.loadedFilteredJobIds) {
            await this.load();
        }
        return this.getFilteredItems();
    }

    setAssignmentId(assignmentId: number | null): this {
        if (assignmentId !== this.assignmentId) {
            // this.loadingItems = false;
            if (this.externalItems) {
                this.items = [];
                this.filteredJobIds = [];
                this.externalItems = false;
            }
            this.loadedItems = false;
            this.loadedFilteredJobIds = false;
        }
        this.assignmentId = assignmentId;
        return this;
    }

    setAssignment(assignment: Assignment | null): this {
        this.assignment = assignment;
        if (assignment) {
            this.setAssignmentId(assignment.getId());
        }
        return this;
    }

    setQuery(query: QueriesType): this {
        if (!this.lastQuery || JSON.stringify(this.query) != JSON.stringify(query)) {
            this.query = query;
            this.loadedFilteredJobIds = false;
        }
        return this;
    }

    async prepareData(day: string, forceReload: boolean = false): Promise<boolean> {
        this.setDay(day);
        if (forceReload) {
            // this.loadingItems = false;
            this.loadedItems = false;
            this.loadedFilteredJobIds = false;
            this.lastQuery = null;
        }
        if (!this.loadedItems || !this.loadedFilteredJobIds) {
            await this.load();
        }
        return true;
    }

    setDay(day: string): this {
        this.day = day;
        return this;
    }

    getItems(): AssignmentJob[] {
        return this.items;
    }

    public getFilteredItems(ordered: boolean = false): AssignmentJob[] {
        if (ordered) {
            this.items.sort((a, b) => {
                return (
                    this.filteredJobIds.indexOf(a.getJobId()) -
                    this.filteredJobIds.indexOf(b.getJobId())
                );
            });
        }
        let filteredItems: AssignmentJob[] = this.items.filter(j =>
            this.filteredJobIds.includes(j.getJobId())
        );
        return filteredItems;
    }

    public getFilteredJobIds(): number[] {
        return this.filteredJobIds.slice();
    }

    updateAssignmentJob(aj: AssignmentJob): this {
        let i = this.items.findIndex(j => j.getJobId() === aj.getJobId());
        if (i >= 0) {
            this.items[i] = aj;
        }
        return this;
    }

    async doAssign(jobIds: number[]) {
        if (this.assignmentId === null) {
            throw new Error('Assignment ID is not set');
        }
        if (this.day === null) {
            throw new Error('Day is not set');
        }
        // await this.doAssignOrReject(jobIds, this.assignmentId);
        let assigned: { assigned: number } = await DeliveriesBulkActions.doAssign(
            this.api,
            this.assignmentId,
            jobIds,
            'selected',
            {},
            this.day
        );
        await this.triggerReload();
        return assigned;
    }

    async doReject(jobIds: number[]) {
        // await this.doAssignOrReject(jobIds, 0);
        if (this.assignmentId === null) {
            throw new Error('Assignment ID is not set');
        }
        if (this.day === null) {
            throw new Error('Day is not set');
        }
        let assigned: { assigned: number } = await DeliveriesBulkActions.doReject(
            this.api,
            jobIds,
            'selected',
            {},
            this.assignmentId,
            this.day
        );
        await this.triggerReload();
        return assigned;
    }

    async setAdhocStatus(jobId: number, adhoc: boolean, day: string | null) {
        if (day !== null) {
            await JobApi.updateTargetDate(this.api, jobId, day);
        }
        await JobApi.setAdhocStatus(this.api, jobId, adhoc);
    }

    getApproachedItems(): AssignmentJob[] {
        return this.items.filter(j => {
            if (j.getRoutePoint() === null) {
                console.warn(
                    '777getRealisationStatus, getApproachedItems, routePoint is null, call method to load route points',
                    j
                );
                return false;
            }
            return DeliveriesMenuActions.isRoutePointApproached(j);
        });
    }

    setMapModule(mapModule: PlanningOnMap | null): this {
        this.mapModule = mapModule;
        return this;
    }

    getMapModule(): PlanningOnMap | null {
        return this.mapModule;
    }

    removeItem(jobId: number) {
        this.items = this.items.filter(j => j.getJobId() !== jobId);
        this.filteredJobIds = this.filteredJobIds.filter(j => j !== jobId);
    }

    setAssignmentRejectedJobs(rejectedJobs: AssignmentJobRejectionResponseType[]) {
        this.assignmentRejectedJobs = {};
        rejectedJobs.forEach((rj) => {
            this.assignmentRejectedJobs[rj.job_id] = new AssignmentJobRejection(rj)
        });
    }

    getRejectedJobs(assignment: boolean = false, jobIds: number[] = []): {[key: number]: AssignmentJobRejection} {
        let rejectedJobs = assignment ? this.assignmentRejectedJobs : this.unhandledRejectedJobs;
        let filteredJobs: {[key: number]: AssignmentJobRejection} = {};
        if (jobIds.length) {
            for (let jobId of jobIds) {
                if (rejectedJobs[jobId]) {
                    filteredJobs[jobId] = rejectedJobs[jobId];
                }
            }
        } else {
            filteredJobs = this.assignmentRejectedJobs;
        }
        return filteredJobs;
    }

    getRejectedJobsAsAssignmentJobs(assignment: boolean = false, jobIds: number[] = []): {[key: number]: AssignmentJob} {
        let rejectedJobs = this.getRejectedJobs(assignment, jobIds);
        let rejectedJobsAsAssignmentJobs: {[key: number]: AssignmentJob} = {};
        for (let jobId in rejectedJobs) {
            rejectedJobsAsAssignmentJobs[jobId] = new AssignmentJob(rejectedJobs[jobId].getAsAssignmentJob());
        }
        return rejectedJobsAsAssignmentJobs;
    }

    async loadUnhandledRejectedJobs() {
        if (this.day) {
            let rejectedJobs = await AssignmentJobRejectionApi.getUnhandledByDay(this.api, this.day);
            this.unhandledRejectedJobs = {};
            for (let rejectedJob of rejectedJobs) {
                this.unhandledRejectedJobs[rejectedJob.job_id] = new AssignmentJobRejection(rejectedJob);
            }
        }
    }

}
